<script>
import ProfileLayout from "@/Layouts/ProfileLayout.vue";

export default {
    layout: ProfileLayout,
};
</script>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { Head, Link, usePage } from "@inertiajs/vue3";
import { useModalogStore } from "@/stores/modalog.js";
import { useWishlistStore } from "@/stores/wishlist.js";
import BrandButton from "@/Components/Brand/BrandButton.vue";
import ConfirmedGiftsTabs from "@/Components/Profile/ConfirmedGiftsTabs.vue";
import GiftCard from "@/Components/Profile/GiftCard.vue";
import ListsPagination from "@/Components/Profile/ListsPagination.vue";
import ProfileHero from "@/Components/Profile/ProfileHero.vue";
import ProfileTabs from "@/Components/Profile/ProfileTabs.vue";
import SortDropdown from "@/Components/Profile/SortDropdown.vue";

const SORT_OPTIONS = [
    { value: "recent", label: "Most Recent" },
    { value: "name-asc", label: "Name A–Z" },
    { value: "name-desc", label: "Name Z–A" },
];

const LISTS_PER_PAGE = 4;

// Access props through the page object so in-place reloads aren't read from
// a stale props reference.
const inertiaPage = usePage();

const owner = computed(() => inertiaPage.props.owner?.data);
const lists = computed(() => inertiaPage.props.lists ?? []);

// The page reveals every purchased gift and who confirmed it, so the grids
// stay behind the same spoiler gate the per-list confirmed view uses —
// including its "Are you sure?" modalog (GH-923). The shared store flag
// resets on mount so revisits are gated again.
const modalogStore = useModalogStore();
const wishlistStore = useWishlistStore();

onMounted(() => {
    wishlistStore.isSpoiled = false;
});

const isSpoiled = computed(() => wishlistStore.isSpoiled);

function openSpoilerModalog() {
    modalogStore.showModalog({
        type: "ModalogConfirmedGiftsSpoiler",
        props: {},
    });
}

const sort = ref("recent");
const page = ref(1);

const sorted = computed(() => {
    const copy = [...lists.value];

    switch (sort.value) {
        case "name-asc":
            return copy.sort((a, b) => a.name.localeCompare(b.name));
        case "name-desc":
            return copy.sort((a, b) => b.name.localeCompare(a.name));
        default:
            // Most recent: latest confirmation first.
            return copy.sort((a, b) =>
                b.latest_confirmed_at.localeCompare(a.latest_confirmed_at),
            );
    }
});

const pages = computed(() =>
    Math.max(1, Math.ceil(sorted.value.length / LISTS_PER_PAGE)),
);

const paged = computed(() =>
    sorted.value.slice(
        (page.value - 1) * LISTS_PER_PAGE,
        page.value * LISTS_PER_PAGE,
    ),
);

watch(sort, () => (page.value = 1));
</script>

<template>
    <div>
        <Head>
            <title>Confirmed Gifts | Gift Hero</title>
        </Head>

        <ProfileHero
            :owner="owner"
            :is-owner="true"
            :theme="owner.profile_theme"
        />

        <ProfileTabs :owner="owner" :is-owner="true" active="confirmed" />

        <div class="px-4 pb-8 sm:px-[35px] sm:pb-9">
            <section
                class="rounded-brand bg-brand-cream px-4 py-6 sm:px-[46px] sm:py-8"
            >
                <header
                    class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
                >
                    <h2 class="font-brand text-[25px] font-bold text-[#102a3d]">
                        Confirmed Gifts
                    </h2>
                    <ConfirmedGiftsTabs active="for-me" />
                    <SortDropdown
                        v-model="sort"
                        :options="SORT_OPTIONS"
                        class="lg:shrink-0"
                    />
                </header>

                <!-- Spoiler gate -->
                <div
                    v-if="!isSpoiled"
                    class="flex flex-col items-center gap-5 py-14 text-center"
                >
                    <h3 class="font-brand text-[25px] font-bold text-[#102a3d]">
                        Spoiler Alert!
                    </h3>
                    <p class="max-w-md font-brand text-[16px] text-[#565a77]">
                        Revealing this view shows
                        <strong>all gifts</strong> that have been confirmed for
                        you and who confirmed them. If you&rsquo;d rather be
                        surprised, look away now!
                    </p>
                    <BrandButton
                        class="h-[52px] px-6 text-[15px]"
                        @click="openSpoilerModalog"
                    >
                        View Confirmed Gifts?
                    </BrandButton>
                </div>

                <template v-else>
                    <template v-if="lists.length">
                        <article
                            v-for="list in paged"
                            :key="list.uuid"
                            class="mt-7 rounded-[5px] bg-white/60 px-4 py-6 sm:px-9"
                        >
                            <header
                                class="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between"
                            >
                                <Link
                                    :href="list.url"
                                    class="font-brand text-[25px] font-bold text-[#102a3d] underline transition hover:text-brand-red"
                                >
                                    {{ list.name }}
                                </Link>
                                <p
                                    class="font-brand text-[15px] text-[#102a3d]"
                                >
                                    <strong
                                        >{{ list.confirmed_count }} of the
                                        {{ list.total_count }}</strong
                                    >
                                    {{
                                        list.total_count === 1
                                            ? "item"
                                            : "items"
                                    }}
                                    on your list have been confirmed as
                                    purchased.
                                </p>
                            </header>

                            <div
                                class="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                            >
                                <GiftCard
                                    v-for="item in list.items"
                                    :key="item.uuid"
                                    :item="item"
                                    :list="{ uuid: list.uuid, name: list.name }"
                                    :spoiled="true"
                                    :confirmed="true"
                                />
                            </div>
                        </article>

                        <ListsPagination
                            v-if="pages > 1"
                            v-model:page="page"
                            :pages="pages"
                            class="mt-10"
                        />
                    </template>

                    <div
                        v-else
                        class="flex flex-col items-center gap-5 py-14 text-center"
                    >
                        <p
                            class="max-w-md font-brand text-[16px] text-[#565a77]"
                        >
                            No gifts on your wish lists have been confirmed as
                            purchased yet. Why not share a wish list with your
                            friends?
                        </p>
                    </div>
                </template>
            </section>
        </div>
    </div>
</template>
